import React from "react";
import { Link } from "react-router-dom";
import { Mail, PhoneCall, ArrowUpRight, Shield, FileText, Rocket } from "lucide-react";
import Logo from "@/components/Logo";

function FooterCol({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
        <Icon className="h-3.5 w-3.5 text-indigo-400" /> {title}
      </div>
      <ul className="space-y-2.5 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="text-white/65 hover:text-white inline-flex items-center gap-1 group">
        {children}
        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-60 transition" />
      </Link>
    </li>
  );
}

function PayBadge({ label, color, wide = false, children }) {
  const isLight = color === "#ffffff";
  return (
    <div
      data-testid={`pay-${label.toLowerCase().replace(/\s+/g, "-")}`}
      title={label}
      className={`h-9 ${wide ? "px-3" : "w-12"} rounded-md grid place-items-center shadow-md ${
        isLight ? "border border-white/15" : ""
      }`}
      style={{ background: color }}
    >
      {children}
    </div>
  );
}

export default function LandingFooter() {
  return (
    <footer className="border-t border-white/5 mt-10 bg-[#06060d]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          <div className="lg:col-span-2">
            <Logo size="h-24 lg:h-28" hideText />
            <p className="mt-4 text-sm text-white/60 max-w-sm">
              The AI digital marketing agency. Content, design, SEO, campaigns and analytics — running 24/7 in your brand voice.
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm text-white/60">
              <a href="mailto:sales@makeyourbrand.in" className="hover:text-white inline-flex items-center gap-2" data-testid="footer-email">
                <Mail className="h-4 w-4" /> sales@makeyourbrand.in
              </a>
              <a href="tel:+918100671006" className="hover:text-white inline-flex items-center gap-2" data-testid="footer-phone">
                <PhoneCall className="h-4 w-4" /> +91 81006 71006
              </a>
            </div>
            <div className="mt-5 text-xs text-white/30 font-mono">
              © {new Date().getFullYear()} SURAJIT MAJHI HUF · makeyourbrand.in
            </div>
          </div>

          <FooterCol icon={Rocket} title="Product">
            <FooterLink to="/features">Features</FooterLink>
            <FooterLink to="/how-it-works">How it works</FooterLink>
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/register">Get started</FooterLink>
            <FooterLink to="/login">Sign in</FooterLink>
          </FooterCol>

          <FooterCol icon={FileText} title="Resources">
            <FooterLink to="/faq">FAQ</FooterLink>
            <FooterLink to="/dashboard/seo">SEO Studio</FooterLink>
            <FooterLink to="/dashboard/crm">CRM</FooterLink>
            <FooterLink to="/dashboard/power-hour">Power Hour</FooterLink>
            <FooterLink to="/dashboard/whitelabel">White-label</FooterLink>
          </FooterCol>

          <FooterCol icon={Shield} title="Legal & Company">
            <FooterLink to="/legal/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/legal/terms">Terms of Service</FooterLink>
            <FooterLink to="/legal/refund">Refund Policy</FooterLink>
            <FooterLink to="/dashboard/support">Support</FooterLink>
          </FooterCol>
        </div>

        <div className="border-t border-white/5 pt-6 space-y-4">
          <div
            className="glass-strong border border-orange-500/30 rounded-2xl p-5 lg:p-6 bg-gradient-to-br from-orange-500/[0.04] to-transparent"
            data-testid="footer-legal-entity-box"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400/80 mb-2 font-semibold">
              Legal Disclosure
            </div>
            <p
              className="text-sm lg:text-base font-bold text-white leading-relaxed"
              data-testid="footer-legal-entity"
            >
              <span className="text-orange-300/90 font-bold">Legal Disclosure:</span>{" "}
              MakeYourBrand.IN is a business unit of SURAJIT MAJHI HUF. Online payments are securely processed via Razorpay under the merchant name Optivize (a unit of SURAJIT MAJHI). The registered banking entity is SURAJIT MAJHI HUF, registered under PAN: <span className="font-mono text-cyan-300">ABOHS4345H</span>.
            </p>
          </div>

          {/* Payment methods strip */}
          <div className="glass rounded-2xl p-5 lg:p-6" data-testid="footer-payments-strip">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-semibold mb-1">
                  Secure Payments
                </div>
                <div className="text-sm text-white/80 font-bold">
                  Powered by <span className="text-cyan-300">Razorpay</span> · 256-bit TLS · PCI-DSS Level 1
                </div>
              </div>
              <div className="flex items-center gap-2 lg:gap-3 flex-wrap justify-center">
                <PayBadge label="Razorpay" color="#0b4ec4">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white">
                    <path d="M8.6 7L7 14h2.4l-1 4 6-9h-3.5L13 7H8.6z" />
                  </svg>
                </PayBadge>
                <PayBadge label="VISA" color="#1A1F71" wide>
                  <span className="text-white font-black italic text-sm tracking-tight">VISA</span>
                </PayBadge>
                <PayBadge label="Mastercard" color="#ffffff">
                  <span className="relative inline-flex">
                    <span className="h-4 w-4 rounded-full bg-[#EB001B]" />
                    <span className="h-4 w-4 rounded-full bg-[#F79E1B] -ml-2 mix-blend-multiply" />
                  </span>
                </PayBadge>
                <PayBadge label="AMEX" color="#006FCF" wide>
                  <span className="text-white font-black text-[10px] tracking-widest">AMEX</span>
                </PayBadge>
                <PayBadge label="UPI" color="#ffffff" wide>
                  <span className="font-black text-xs">
                    <span style={{ color: "#FF8000" }}>U</span>
                    <span style={{ color: "#000" }}>P</span>
                    <span style={{ color: "#0e7b3f" }}>I</span>
                  </span>
                </PayBadge>
                <PayBadge label="Net Banking" color="#0f766e">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white">
                    <path d="M3 9l9-5 9 5v2H3V9zm2 3h2v6H5v-6zm4 0h2v6H9v-6zm4 0h2v6h-2v-6zm4 0h2v6h-2v-6zM3 19h18v2H3v-2z"/>
                  </svg>
                </PayBadge>
                <PayBadge label="RuPay" color="#0e2c87" wide>
                  <span className="text-white font-black text-[10px] tracking-wider">Ru<span className="text-orange-400">P</span>ay</span>
                </PayBadge>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40 font-mono uppercase tracking-[0.2em]">
            <div>Built in India · For brands worldwide</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span>Razorpay secured</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>GDPR-ready</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>SOC 2 (in progress)</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
